import styled from "styled-components";

export const Container = styled.section`
  display: flex;
  justify-content: space-between;
  gap: 6rem;
  background: transparent;
  min-height: 100vh;
  padding-top: 10rem;
  align-items: center;

  /* ── Hero Text (left column) ── */
  .hero-text {
    flex: 1;
    max-width: 620px;
    text-align: left;

    /* Monospace eyebrow — $ whoami */
    .hero-eyebrow {
      display: block;
      font-family: var(--font-code);
      font-size: 1.3rem;
      color: var(--accent-teal);
      letter-spacing: 0.08em;
      margin-bottom: 1.6rem;
      opacity: 0.8;
    }

    .hero-greeting {
      font-size: 1.8rem;
      color: var(--text-muted);
      margin-bottom: 0.6rem;
      font-family: var(--font-body);
      display: flex;
      align-items: center;
      gap: 0.5rem;

      img { vertical-align: middle; }
    }

    h1 {
      font-family: var(--font-heading);
      font-size: 8rem;
      font-weight: 800;
      line-height: 1.0;
      letter-spacing: -0.04em;
      margin-bottom: 2rem;
    }

    .hero-tagline {
      font-family: var(--font-heading);
      font-size: 2rem;
      font-weight: 600;
      color: var(--accent-teal);
      letter-spacing: 0.01em;
      margin-bottom: 1.8rem;
    }

    .hero-description {
      font-size: 1.8rem;
      line-height: 1.65;
      margin-bottom: 3.5rem;
      color: var(--text-muted);
      font-weight: 400;
      max-width: 560px;

      .light & { color: var(--text-muted); }

      .highlight {
        color: var(--text-primary);
        font-weight: 500;
      }
    }

    /* "Open to Work" badge */
    .open-to-work-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.7rem;
      background: rgba(166, 218, 149, 0.08);
      border: 1.5px solid rgba(166, 218, 149, 0.3);
      border-radius: 6px;
      padding: 0.4rem 1.2rem 0.4rem 0.9rem;
      font-family: var(--font-code);
      font-size: 1.2rem;
      font-weight: 500;
      color: var(--accent-green);
      letter-spacing: 0.04em;
      margin-bottom: 2rem;

      .light & {
        background: rgba(64, 160, 43, 0.08);
        border-color: rgba(64, 160, 43, 0.3);
        color: var(--accent-green);
      }
    }

    .signal-dot {
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;
      background: var(--accent-green);
      box-shadow: 0 0 6px var(--accent-green);
      flex-shrink: 0;
      animation: signal-pulse 2s ease-in-out infinite;
    }

    @keyframes signal-pulse {
      0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(166, 218, 149, 0.7); }
      50%       { transform: scale(1.15); box-shadow: 0 0 0 5px rgba(166, 218, 149, 0); }
    }
  }

  /* ── Hero CTA Button ── */
  .button.hero-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
    padding: 1.3rem 3.2rem;
    border-radius: 8px;
    background: var(--accent-mauve);
    color: var(--bg-base) !important;
    font-size: 1.6rem;
    font-weight: 700;
    font-family: var(--font-body);
    letter-spacing: 0.01em;
    box-shadow: 0 8px 24px rgba(198, 160, 246, 0.3);
    transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    position: relative;
    overflow: hidden;
    margin-top: 0;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(120deg, transparent, rgba(255,255,255,0.18), transparent);
      transition: left 0.5s ease;
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 14px 30px rgba(198, 160, 246, 0.45);

      &::before { left: 100%; }

      .arrow-icon { transform: translateX(4px); }
    }

    .light & {
      box-shadow: 0 8px 24px rgba(136, 57, 239, 0.25);
    }
  }

  .arrow-icon {
    display: flex;
    align-items: center;
    transition: transform 0.25s ease;
  }

  /* ── Social links ── */
  .social-media {
    display: inline-flex;
    align-items: center;
    gap: 1.2rem;
    padding-top: 2.5rem;

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      background: rgba(198, 160, 246, 0.06);
      border: 1px solid rgba(198, 160, 246, 0.14);
      transition: all 0.2s ease;

      img {
        width: 2rem;
        height: 2rem;
        opacity: 0.85;
        transition: opacity 0.2s ease, transform 0.2s ease;
        object-fit: contain;
      }

      &:hover {
        background: rgba(198, 160, 246, 0.14);
        border-color: var(--accent-mauve);
        transform: translateY(-2px);

        img { opacity: 1; }
      }
    }
  }

  /* ── Hero Image / Right Column ── */
  .hero-image {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    align-items: flex-end;
    flex-shrink: 0;
  }

  /* ─────────────────────────────────────────────────────────
   * SYSTEM STATUS PANEL — Hirelytics signature element
   * ───────────────────────────────────────────────────────── */
  .status-panel {
    width: 360px;
    background: var(--bg-surface);
    border: 1px solid rgba(198, 160, 246, 0.18);
    border-radius: 12px;
    padding: 2rem 2rem 1.6rem;
    box-shadow:
      0 20px 50px rgba(0,0,0,0.4),
      0 0 0 1px rgba(198, 160, 246, 0.06) inset;
    animation: float-gentle 7s ease-in-out infinite;
    position: relative;

    &::before {
      /* Scan line on hover — subtle tech effect */
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: linear-gradient(
        180deg,
        rgba(198, 160, 246, 0) 0%,
        rgba(198, 160, 246, 0.04) 50%,
        rgba(198, 160, 246, 0) 100%
      );
      background-size: 100% 200%;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }

    &:hover::before {
      opacity: 1;
      animation: scan-line 1.5s linear infinite;
    }

    .light & {
      background: var(--bg-surface);
      border-color: rgba(136, 57, 239, 0.15);
      box-shadow: 0 16px 40px rgba(0,0,0,0.08), 0 0 0 1px rgba(136, 57, 239, 0.06) inset;
    }

    .panel-header {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      margin-bottom: 1.6rem;
      padding-bottom: 1.4rem;
      border-bottom: 1px solid rgba(198, 160, 246, 0.1);

      .light & { border-bottom-color: rgba(136, 57, 239, 0.1); }

      .panel-comment {
        font-family: var(--font-code);
        font-size: 1.3rem;
        color: var(--accent-mauve);
        letter-spacing: 0.02em;
      }

      .panel-subtitle {
        font-family: var(--font-code);
        font-size: 1.1rem;
        color: var(--text-muted);
        letter-spacing: 0.03em;
      }
    }

    .panel-modules {
      display: flex;
      flex-direction: column;
      gap: 1.1rem;
      margin-bottom: 1.6rem;
    }

    .module-row {
      display: flex;
      align-items: center;
      gap: 1rem;
      font-family: var(--font-code);
      font-size: 1.25rem;
    }

    .module-dot {
      width: 0.7rem;
      height: 0.7rem;
      border-radius: 50%;
      flex-shrink: 0;
      animation: module-pulse 2.5s ease-in-out infinite;
    }

    .module-dot:nth-child(1) { animation-delay: 0s; }
    .module-dot:nth-child(2) { animation-delay: 0.4s; }
    .module-dot:nth-child(3) { animation-delay: 0.8s; }

    .module-name {
      flex: 1;
      color: var(--text-primary);
      letter-spacing: 0.02em;
    }

    .module-status {
      font-size: 1.1rem;
      font-weight: 600;
      letter-spacing: 0.04em;
    }

    .panel-footer {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      padding-top: 1.2rem;
      border-top: 1px solid rgba(198, 160, 246, 0.1);

      .light & { border-top-color: rgba(136, 57, 239, 0.1); }

      .stat-line {
        font-family: var(--font-code);
        font-size: 1.15rem;
        color: var(--text-muted);
        letter-spacing: 0.02em;

        strong {
          color: var(--accent-teal);
          font-weight: 600;
        }
      }
    }
  }

  @keyframes float-gentle {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-10px); }
  }

  @keyframes module-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.55; }
  }

  @keyframes scan-line {
    0%   { background-position: 0% 0%; }
    100% { background-position: 0% 200%; }
  }

  /* ─────────────────────────────────────────────────────────
   * MINI CODE CARD — secondary visual below status panel
   * ───────────────────────────────────────────────────────── */
  .code-card-mini {
    width: 360px;
    background: var(--bg-overlay);
    border: 1px solid rgba(198, 160, 246, 0.12);
    border-radius: 10px;
    padding: 1.2rem 1.4rem;
    animation: float-gentle 8s ease-in-out infinite;
    animation-delay: -3s;

    .light & {
      background: var(--bg-surface);
      border-color: rgba(136, 57, 239, 0.12);
    }

    .code-header {
      display: flex;
      align-items: center;
      gap: 6px;
      padding-bottom: 1rem;
      border-bottom: 1px solid rgba(198, 160, 246, 0.08);
      margin-bottom: 1.2rem;

      .light & { border-bottom-color: rgba(136, 57, 239, 0.08); }

      .dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        &.red    { background: #ed8796; }
        &.yellow { background: #eed49f; }
        &.green  { background: #a6da95; }
      }

      .code-filename {
        font-family: var(--font-code);
        font-size: 1.1rem;
        color: var(--text-muted);
        margin-left: 0.6rem;
        letter-spacing: 0.03em;
      }
    }

    .code-content {
      font-family: var(--font-code);
      font-size: 1.3rem;
      line-height: 1.7;

      .keyword  { color: #c6a0f6; }  /* mauve */
      .variable { color: #ed8796; }  /* red */
      .string   { color: #a6da95; }  /* green */
      pre { margin: 0; white-space: pre-wrap; }
      code { color: var(--text-primary); }
    }

    .cta-container {
      display: flex;
      gap: 1rem;
      margin-top: 1.4rem;
      padding-top: 1.2rem;
      border-top: 1px solid rgba(198, 160, 246, 0.08);

      & > div { flex: 1; display: flex !important; }

      .light & { border-top-color: rgba(136, 57, 239, 0.08); }

      .cta-button {
        flex: 1;
        padding: 0.8rem;
        border-radius: 7px;
        text-align: center;
        font-size: 1.3rem;
        font-weight: 600;
        transition: all 0.2s ease;

        &.primary {
          background: var(--accent-mauve);
          color: var(--bg-base);
          border: none;
          &:hover { opacity: 0.85; transform: translateY(-2px); }
        }

        &.secondary {
          background: transparent;
          color: var(--text-muted);
          border: 1px solid rgba(198, 160, 246, 0.2);
          &:hover {
            border-color: var(--accent-mauve);
            color: var(--accent-mauve);
            transform: translateY(-2px);
          }

          .light & {
            color: var(--text-muted);
            border-color: rgba(136, 57, 239, 0.2);
            &:hover { border-color: var(--accent-mauve); color: var(--accent-mauve); }
          }
        }
      }
    }
  }

  /* ─────────────────────────────────────────────────────────
   * RESPONSIVE — 1200px
   * ───────────────────────────────────────────────────────── */
  @media (max-width: 1200px) and (min-width: 961px) {
    gap: 4rem;

    .hero-text h1 { font-size: 6.5rem; }

    .status-panel, .code-card-mini { width: 320px; }
  }

  /* ─────────────────────────────────────────────────────────
   * RESPONSIVE — 960px (tablet/mobile)
   * ───────────────────────────────────────────────────────── */
  @media (max-width: 960px) {
    flex-direction: column;
    text-align: center;
    padding-top: 9rem;
    min-height: auto;
    gap: 3rem;
    align-items: center;

    .hero-text {
      max-width: 100%;
      width: 100%;
      text-align: center;

      .hero-eyebrow { text-align: center; }
      .hero-greeting { justify-content: center; }
      .hero-tagline { font-size: 1.8rem; }
      .hero-description { margin-left: auto; margin-right: auto; font-size: 1.7rem; }

      h1 { font-size: 5.5rem; }

      .open-to-work-badge {
        display: inline-flex;
        justify-content: center;
        margin-left: auto;
        margin-right: auto;
      }
    }

    .button.hero-cta { margin-left: auto; margin-right: auto; }
    .social-media    { justify-content: center; }

    .hero-image {
      width: 100%;
      align-items: center;
    }

    .status-panel, .code-card-mini { width: min(90vw, 400px); }
  }

  @media (max-width: 480px) {
    padding-top: 8rem;
    gap: 2.4rem;

    .hero-text {
      h1 { font-size: 4rem; }
      .hero-tagline { font-size: 1.6rem; }
      .hero-description { font-size: 1.55rem; }
    }

    .button.hero-cta {
      width: 100%;
      max-width: 280px;
      justify-content: center;
    }
  }

  @media (max-width: 380px) {
    .hero-text h1 { font-size: 3.4rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .status-panel, .code-card-mini { animation: none; }
    .signal-dot { animation: none; }
    .module-dot { animation: none; }
    .button.hero-cta { animation: none; }
  }
`;